/**
 * FAZ A — Depo Yönetim Ekranı (/depolar)
 *
 * Mevcut backend altyapısını reuse eder:
 *   GET/POST/PATCH/DELETE /api/warehouses
 *   GET /api/inventory/stock?warehouse_id=...
 *
 * Yeni paralel sistem yazılmadı — sadece UI eksikliği tamamlandı.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Warehouse as WarehouseIcon, Plus, Trash2, Edit3, Loader2, Search, MapPin, Package, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { matchesAll } from "@/lib/trFold";
import WarehouseDetailDialog from "@/components/WarehouseDetailDialog";
import WarehouseTransferDialog from "@/components/WarehouseTransferDialog";
import StockCountDialog from "@/components/StockCountDialog";

const emptyForm = { id: "", code: "", name: "", address: "", is_active: true };

export default function Warehouses() {
  const { can } = useAuth();
  const canAdd = can("warehouse", "add");
  const canEdit = can("warehouse", "edit");
  const canDelete = can("warehouse", "delete");

  const [rows, setRows] = useState([]);
  const [stockSummary, setStockSummary] = useState({});
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailWh, setDetailWh] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferSrc, setTransferSrc] = useState("");
  const [countOpen, setCountOpen] = useState(false);
  const [countWh, setCountWh] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      // FAZ A: tek aggregated endpoint — N+1 çağrı problemi çözüldü
      const { data } = await api.get("/warehouses/summary");
      const list = Array.isArray(data) ? data : [];
      setRows(list);
      const summary = {};
      for (const w of list) {
        summary[w.id] = { items: w.stock_items || 0, total_qty: w.total_qty || 0 };
      }
      setStockSummary(summary);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    return rows.filter((r) => matchesAll(q, [r.code, r.name, r.address]));
  }, [rows, q]);

  const openNew = () => { setForm(emptyForm); setOpen(true); };
  const openEdit = (w) => { setForm({ ...emptyForm, ...w }); setOpen(true); };

  const submit = async () => {
    if (!form.code.trim()) { toast.error("Depo kodu zorunludur"); return; }
    if (!form.name.trim()) { toast.error("Depo adı zorunludur"); return; }
    try {
      setSaving(true);
      if (form.id) {
        const patch = {
          code: form.code, name: form.name, address: form.address, is_active: form.is_active,
        };
        await api.patch(`/warehouses/${form.id}`, patch);
        toast.success("Depo güncellendi");
      } else {
        await api.post("/warehouses", {
          code: form.code, name: form.name, address: form.address, is_active: form.is_active,
        });
        toast.success("Depo oluşturuldu");
      }
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (w) => {
    const summary = stockSummary[w.id];
    if (summary && summary.items > 0) {
      toast.error(`Bu depoda ${summary.items} stok kaydı var. Önce boşaltın.`);
      return;
    }
    if (!window.confirm(`"${w.code} · ${w.name}" silinsin mi?`)) return;
    try {
      await api.delete(`/warehouses/${w.id}`);
      toast.success("Depo silindi");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <PageHeader
        title="Depolar"
        subtitle="Fiziksel depo lokasyonları · Stok yönetimi bağlantısı"
      />
      <div className="p-6 lg:p-8 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-500" />
            <Input
              data-testid="warehouse-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Depo ara (kod, isim, adres)"
              className="bg-slate-950 border-slate-700 rounded-sm pl-8 font-mono"
            />
          </div>
          {canEdit && (
            <Button data-testid="open-transfer-btn" onClick={() => { setTransferSrc(""); setTransferOpen(true); }} variant="outline" className="border-slate-700 rounded-sm text-xs">
              <ArrowRightLeft className="w-4 h-4 mr-1.5" /> Depolar Arası Transfer
            </Button>
          )}
          {canAdd && (
            <Button
              data-testid="add-warehouse-btn"
              onClick={openNew}
              className="bg-blue-500 hover:bg-blue-600 text-slate-950 rounded-sm font-semibold"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Yeni Depo
            </Button>
          )}
        </div>

        <Card className="border-slate-800 bg-slate-950/40 rounded-sm">
          {loading ? (
            <div className="p-12 flex items-center justify-center text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Yükleniyor…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <WarehouseIcon className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <div className="text-sm">
                {rows.length === 0 ? "Henüz depo yok. \"Yeni Depo\" ile ekleyin." : "Aramaya uyan depo bulunamadı."}
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800">
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Kod</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Depo Adı</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Adres</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Stok Kalemi</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Toplam Adet</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Durum</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((w) => {
                  const st = stockSummary[w.id] || { items: 0, total_qty: 0 };
                  return (
                    <TableRow
                      key={w.id}
                      className="border-slate-800 hover:bg-slate-900/40 cursor-pointer"
                      onClick={(e) => { if (!(e.target.closest("button"))) { setDetailWh(w); setDetailOpen(true); } }}
                      data-testid={`warehouse-row-${w.id}`}
                    >
                      <TableCell className="font-mono text-xs text-blue-400">{w.code}</TableCell>
                      <TableCell className="text-sm text-slate-100 font-semibold">{w.name}</TableCell>
                      <TableCell className="text-xs text-slate-400 flex items-center gap-1">
                        {w.address ? (<><MapPin className="w-3 h-3 text-slate-600" />{w.address}</>) : <span className="text-slate-600">—</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs text-slate-300">{st.items}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-emerald-400">{st.total_qty.toLocaleString("tr-TR")}</TableCell>
                      <TableCell>
                        {w.is_active !== false ? (
                          <Badge className="bg-emerald-950 text-emerald-300 border-emerald-800 rounded-sm text-[9px]">AKTİF</Badge>
                        ) : (
                          <Badge className="bg-slate-800 text-slate-400 border-slate-700 rounded-sm text-[9px]">PASİF</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canEdit && (
                            <Button
                              data-testid={`edit-warehouse-${w.id}`}
                              variant="ghost" size="sm"
                              onClick={() => openEdit(w)}
                              className="h-7 px-2 text-slate-300 hover:text-blue-400"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              data-testid={`delete-warehouse-${w.id}`}
                              variant="ghost" size="sm"
                              onClick={() => del(w)}
                              className="h-7 px-2 text-slate-300 hover:text-rose-400"
                              disabled={st.items > 0}
                              title={st.items > 0 ? "Depoda stok var" : "Sil"}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      <WarehouseDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        warehouse={detailWh}
        canEdit={canEdit}
        onStartTransfer={(w) => { setDetailOpen(false); setTransferSrc(w.id); setTransferOpen(true); }}
        onStartCount={(w) => { setDetailOpen(false); setCountWh(w); setCountOpen(true); }}
      />
      <WarehouseTransferDialog
        open={transferOpen}
        onOpenChange={setTransferOpen}
        warehouses={rows}
        defaultSourceId={transferSrc}
        onDone={load}
      />
      <StockCountDialog
        open={countOpen}
        onOpenChange={setCountOpen}
        warehouse={countWh}
        onDone={load}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-950 border-slate-800 rounded-sm max-w-md" data-testid="warehouse-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <WarehouseIcon className="w-4 h-4 text-blue-400" />
              {form.id ? "Depo Düzenle" : "Yeni Depo"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Fiziksel bir depo lokasyonu tanımlayın. Stok kayıtları bu depoya bağlanır.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
                Depo Kodu<span className="text-rose-400 ml-1">*</span>
              </Label>
              <Input
                data-testid="warehouse-code-input"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="MERKEZ / ANA / D01"
                className="bg-slate-950 border-slate-700 rounded-sm font-mono uppercase"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
                Depo Adı<span className="text-rose-400 ml-1">*</span>
              </Label>
              <Input
                data-testid="warehouse-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Örn. Merkez Depo — İstanbul"
                className="bg-slate-950 border-slate-700 rounded-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Adres</Label>
              <Textarea
                data-testid="warehouse-address-input"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Depo adresi (opsiyonel)"
                className="bg-slate-950 border-slate-700 rounded-sm text-sm"
                rows={2}
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-300 select-none cursor-pointer">
              <input
                data-testid="warehouse-active-checkbox"
                type="checkbox"
                checked={form.is_active !== false}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="accent-blue-500"
              />
              <span>Aktif (satış sihirbazında listelenir)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">İptal</Button>
            <Button
              data-testid="warehouse-save-btn"
              onClick={submit}
              disabled={saving}
              className="bg-blue-500 hover:bg-blue-600 text-slate-950 rounded-sm font-semibold"
            >
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              {form.id ? "Güncelle" : "Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
